import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiSearch, FiX, FiClock, FiArrowRight, FiRss, FiFileText, FiHelpCircle, FiBookOpen, FiTrash2, FiGrid, FiCornerDownLeft,
    FiUser, FiLifeBuoy, FiClipboard, FiAward, FiStar, FiBarChart2, FiBriefcase, FiFolder,
} from "react-icons/fi";
import { useUI } from "../../hooks/useUI";
import { apiService } from "../../api/apiService";
import { localizedPath } from "../../hooks/useLocalizedNavigate";
import useKafelkiWyszukiwarki, { kafelekPasuje } from "../../hooks/useKafelkiWyszukiwarki";
import useNajnowszaOdpowiedz from "../../hooks/useNajnowszaOdpowiedz";
import { wczytajHistorie, zapiszWHistorii, usunZHistorii, wyczyscHistorie } from "../../utils/historiaWyszukiwania";
import { shakeModal } from "../../utils/modalAttention";
import SideDrawer from "../SideDrawer/SideDrawer";
import styles from "./WyszukiwarkaGlobalna.module.css";

/**
 * [S6, polecenie właściciela 2026-09-13] WYSZUKIWARKA GLOBALNA z nagłówka:
 * „powinna przeszukiwać treści i podpowiadać kafelki oraz być responsywna w zakresie poszukiwań treści oraz
 *  ustawień, do których pracownik ma uprawnienia i tylko takie sekcje powinna przeszukiwać. Wyszukiwanie
 *  dynamiczne w zakresie aplikacji, po wpisaniu z historią w oknie modal ostatnio wyszukiwanych pozycji.
 *  Wyniki wyszukiwania w sekcji ala szuflada."
 *
 * [X-H, polecenie właściciela 2026-09-14] „okno wyszukiwarki ładniejsze: ostatnio wypisane frazy pod polem, na środku,
 * większe — z animacją wyszukiwania” oraz „zrobioną wyszukiwarkę, o której gadaliśmy, i co zostało z początkowego briefa”:
 *  • okno większe (52 rem), pole 1.15 rem; ostatnie frazy jako pigułki WYŚRODKOWANE pod polem, pod nimi żetony zakresu
 *    (co przeszukujemy — lista rośnie razem z uprawnieniami);
 *  • animacja szukania: pulsująca lupa, pasek postępu pod polem i szkielety w miejscu wyników (nic nie skacze);
 *  • trafienie podświetlone w tytule;
 *  • zakres treści rozstrzygnięty (otwarta decyzja S6): oprócz aktualności/dokumentów/bazy wiedzy/FAQ także OSOBY z książki
 *    adresowej, WŁASNE zgłoszenia i wnioski HR, szkolenia, konkursy, ankiety, oferty pracy i projekty, w których jestem —
 *    serwer oddaje wyłącznie to, co pytający ma prawo zobaczyć (`GET api/search`).
 *
 * Trzy warstwy:
 *  1. OKNO (modal) — pole, historia ostatnich fraz (localStorage), podpowiedzi KAFELKÓW liczone z tych samych
 *     reguł, którymi rysuje się menu (useKafelkiWyszukiwarki) i pierwsze trafienia TREŚCI z serwera;
 *  2. SZUFLADA — pełne wyniki pogrupowane rodzajem (Enter albo „Pokaż wszystkie”);
 *  3. TELEFON — okna nie ma wcale: lupa w nagłówku otwiera „Szybki dostęp” (decyzja w Header.js).
 *
 * Wyszukiwanie jest DYNAMICZNE (300 ms po ostatnim znaku, od 2 znaków), ze strażnikiem kolejności odpowiedzi —
 * wolniejsza odpowiedź na starszą frazę nie nadpisze nowszej.
 */
export const RODZAJE = {
    aktualnosc: { etykieta: "Aktualności", Ikona: FiRss },
    dokument: { etykieta: "Dokumenty", Ikona: FiFileText },
    artykul: { etykieta: "Baza wiedzy", Ikona: FiBookOpen },
    faq: { etykieta: "FAQ", Ikona: FiHelpCircle },
    osoba: { etykieta: "Osoby", Ikona: FiUser },
    zgloszenie: { etykieta: "Moje zgłoszenia", Ikona: FiLifeBuoy },
    wniosek: { etykieta: "Moje wnioski", Ikona: FiClipboard },
    szkolenie: { etykieta: "Szkolenia", Ikona: FiAward },
    konkurs: { etykieta: "Konkursy", Ikona: FiStar },
    ankieta: { etykieta: "Ankiety", Ikona: FiBarChart2 },
    oferta: { etykieta: "Oferty pracy", Ikona: FiBriefcase },
    projekt: { etykieta: "Projekty", Ikona: FiFolder },
};

/** Żetony zakresu na ekranie startowym — co przeszukujemy (kolejność jak sekcje wyników). */
export const ZAKRES = [
    { klucz: "strony", etykieta: "Strony i ustawienia", Ikona: FiGrid },
    ...Object.entries(RODZAJE).map(([klucz, r]) => ({ klucz, etykieta: r.etykieta, Ikona: r.Ikona })),
];

export const MIN_FRAZA = 2;
export const OPOZNIENIE_MS = 300;
export const LIMIT_W_OKNIE = 3;

/** Adres pozycji z serwera: klucz trasy + parametr (:slug / :id) + doklejone zapytanie (?doc=…). */
export const sciezkaPozycji = (p) => {
    const parametry = p.parametr ? { slug: p.parametr, id: p.parametr } : {};
    return localizedPath(p.trasa, parametry) + (p.zapytanie || "");
};

export const grupujTresci = (tresci) => {
    const grupy = {};
    (tresci || []).forEach((p) => {
        const klucz = RODZAJE[p.rodzaj] ? p.rodzaj : "artykul";
        if (!grupy[klucz]) grupy[klucz] = [];
        grupy[klucz].push(p);
    });
    return Object.keys(RODZAJE).filter((k) => grupy[k]?.length).map((k) => ({ rodzaj: k, ...RODZAJE[k], pozycje: grupy[k] }));
};

/** Podświetlenie trafienia w tytule (bez regexu na frazie użytkownika — porównanie na małych literach). */
export const Podswietl = ({ tekst, fraza }) => {
    const t = String(tekst || "");
    const f = String(fraza || "").trim();
    if (!f || f.length < MIN_FRAZA) return t;
    const i = t.toLocaleLowerCase("pl-PL").indexOf(f.toLocaleLowerCase("pl-PL"));
    if (i < 0) return t;
    return (
        <>
            {t.slice(0, i)}<mark>{t.slice(i, i + f.length)}</mark>{t.slice(i + f.length)}
        </>
    );
};

const WyszukiwarkaGlobalna = () => {
    const { isWyszukiwarkaOpen, closeWyszukiwarke } = useUI();
    const navigate = useNavigate();
    const kafelki = useKafelkiWyszukiwarki();
    const zacznijZadanie = useNajnowszaOdpowiedz();

    const [fraza, setFraza] = useState("");
    const [historia, setHistoria] = useState([]);
    const [tresci, setTresci] = useState([]);
    const [ladowanie, setLadowanie] = useState(false);
    const [blad, setBlad] = useState("");
    const [szuflada, setSzuflada] = useState(false);
    const poleRef = useRef(null);

    // Otwarcie: świeże pole, świeża historia, ostrość w polu.
    useEffect(() => {
        if (!isWyszukiwarkaOpen) return;
        setFraza("");
        setTresci([]);
        setBlad("");
        setSzuflada(false);
        setHistoria(wczytajHistorie());
        const t = setTimeout(() => poleRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [isWyszukiwarkaOpen]);

    // Dynamiczne szukanie treści — z opóźnieniem i strażnikiem kolejności.
    useEffect(() => {
        if (!isWyszukiwarkaOpen) return undefined;
        const f = fraza.trim();
        if (f.length < MIN_FRAZA) { setTresci([]); setLadowanie(false); return undefined; }
        const aktualne = zacznijZadanie();
        setLadowanie(true);
        const t = setTimeout(async () => {
            try {
                const res = await apiService.szukajGlobalnie(f);
                if (!aktualne()) return;
                setTresci(Array.isArray(res?.data?.tresci) ? res.data.tresci : []);
                setBlad("");
            } catch {
                if (!aktualne()) return;
                setTresci([]);
                setBlad("Nie udało się przeszukać treści — spróbuj ponownie.");
            } finally {
                if (aktualne()) setLadowanie(false);
            }
        }, OPOZNIENIE_MS);
        return () => clearTimeout(t);
    }, [fraza, isWyszukiwarkaOpen, zacznijZadanie]);

    const dopasowaneKafelki = useMemo(
        () => (fraza.trim() ? kafelki.filter((k) => kafelekPasuje(k, fraza)) : []),
        [kafelki, fraza]
    );
    const grupy = useMemo(() => grupujTresci(tresci), [tresci]);
    const liczbaWynikow = dopasowaneKafelki.length + tresci.length;

    const zamknijWszystko = useCallback(() => {
        setSzuflada(false);
        closeWyszukiwarke();
    }, [closeWyszukiwarke]);

    const przejdz = useCallback((sciezka) => {
        setHistoria(zapiszWHistorii(fraza));
        zamknijWszystko();
        navigate(sciezka);
    }, [fraza, navigate, zamknijWszystko]);

    const otworzSzuflade = useCallback(() => {
        if (fraza.trim().length < MIN_FRAZA) return;
        setHistoria(zapiszWHistorii(fraza));
        setSzuflada(true);
    }, [fraza]);

    // Escape zamyka (szufladę przed oknem).
    useEffect(() => {
        if (!isWyszukiwarkaOpen) return undefined;
        const naKlawisz = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            if (szuflada) setSzuflada(false); else closeWyszukiwarke();
        };
        window.addEventListener("keydown", naKlawisz);
        return () => window.removeEventListener("keydown", naKlawisz);
    }, [isWyszukiwarkaOpen, szuflada, closeWyszukiwarke]);

    if (!isWyszukiwarkaOpen) return null;

    const Kafelek = ({ k }) => (
        <button type="button" className={styles.pozycja} onClick={() => przejdz(k.sciezka)} data-kafelek={k.id}>
            <span className={styles.pozycjaIkona}>{k.ikona || <FiGrid />}</span>
            <span className={styles.pozycjaTeksty}>
                <span className={styles.pozycjaTytul}>{k.etykieta}</span>
                <span className={styles.pozycjaOpis}>{k.sekcja}</span>
            </span>
            <FiArrowRight size={14} className={styles.pozycjaStrzalka} />
        </button>
    );

    const Tresc = ({ p, Ikona }) => (
        <button type="button" className={styles.pozycja} onClick={() => przejdz(sciezkaPozycji(p))} data-tresc={`${p.rodzaj}:${p.id}`}>
            <span className={styles.pozycjaIkona}><Ikona /></span>
            <span className={styles.pozycjaTeksty}>
                <span className={styles.pozycjaTytul}><Podswietl tekst={p.tytul} fraza={fraza} /></span>
                {p.fragment && <span className={styles.pozycjaOpis}>{p.fragment}</span>}
            </span>
            <FiArrowRight size={14} className={styles.pozycjaStrzalka} />
        </button>
    );

    const Szkielet = () => (
        <div className={styles.szkielet} aria-hidden="true" data-testid="szkielet-wynikow">
            {[0, 1, 2].map((i) => (
                <div key={i} className={styles.szkieletWiersz}>
                    <span className={styles.szkieletKwadrat} />
                    <span className={`${styles.szkieletLinia} ${i === 1 ? styles.szkieletLiniaKrotka : ""}`} />
                </div>
            ))}
        </div>
    );

    const pusteFraza = fraza.trim().length < MIN_FRAZA;

    return (
        <>
            <div className={styles.overlay} data-testid="tlo-wyszukiwarki" onClick={shakeModal}>
                <div className={styles.okno} role="dialog" aria-modal="true" aria-label="Wyszukiwarka" onClick={(e) => e.stopPropagation()}>
                    <div className={styles.pole}>
                        <span className={`${styles.poleIkona} ${ladowanie ? styles.poleIkonaSzuka : ""}`} data-testid="ikona-szukania" data-szuka={ladowanie ? "1" : "0"}>
                            <FiSearch size={20} />
                        </span>
                        <input
                            ref={poleRef}
                            type="text"
                            className={styles.poleInput}
                            value={fraza}
                            onChange={(e) => setFraza(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") otworzSzuflade(); }}
                            placeholder="Szukaj osób, treści, stron i ustawień…"
                            aria-label="Szukaj w aplikacji"
                            autoComplete="off"
                        />
                        {fraza && (
                            <button type="button" className={styles.poleWyczysc} onClick={() => setFraza("")} title="Wyczyść">
                                <FiX size={16} />
                            </button>
                        )}
                        <button type="button" className={styles.zamknij} onClick={closeWyszukiwarke} title="Zamknij" aria-label="Zamknij wyszukiwarkę">
                            <FiX size={18} />
                        </button>
                        {ladowanie && <span className={styles.pasekPostepu} data-testid="pasek-szukania" />}
                    </div>

                    <div className={styles.tresc}>
                        {pusteFraza ? (
                            <div className={styles.start}>
                                {historia.length > 0 ? (
                                    <section className={styles.start} aria-label="Ostatnio wyszukiwane">
                                        <div className={styles.startNaglowek}>
                                            <FiClock size={12} /> Ostatnio wyszukiwane
                                            <button type="button" className={styles.link} onClick={() => setHistoria(wyczyscHistorie())}>
                                                <FiTrash2 size={11} /> Wyczyść
                                            </button>
                                        </div>
                                        <ul className={styles.historia}>
                                            {historia.map((h) => (
                                                <li key={h}>
                                                    <button type="button" className={styles.historiaPozycja} onClick={() => setFraza(h)}>
                                                        <FiClock size={13} /> {h}
                                                    </button>
                                                    <button type="button" className={styles.historiaUsun} onClick={() => setHistoria(usunZHistorii(h))} title="Usuń z historii" aria-label={`Usuń z historii: ${h}`}>
                                                        <FiX size={12} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                ) : (
                                    <p className={styles.podpowiedz}>
                                        Wpisz co najmniej {MIN_FRAZA} znaki. Szukamy tylko w tym, do czego masz dostęp — i podpowiadamy strony oraz ustawienia.
                                    </p>
                                )}
                                <div className={styles.startNaglowek}>Co przeszukujemy</div>
                                <div className={styles.zakres} data-testid="zakres-wyszukiwania">
                                    {ZAKRES.map((z) => (
                                        <span key={z.klucz} className={styles.zakresZeton}><z.Ikona size={12} /> {z.etykieta}</span>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <>
                                {dopasowaneKafelki.length > 0 && (
                                    <section className={styles.sekcja} aria-label="Strony i ustawienia">
                                        <div className={styles.sekcjaNaglowek}><span><FiGrid size={12} /> Strony i ustawienia</span></div>
                                        {dopasowaneKafelki.slice(0, 6).map((k) => <Kafelek key={k.id} k={k} />)}
                                    </section>
                                )}
                                {grupy.map((g) => (
                                    <section key={g.rodzaj} className={styles.sekcja} aria-label={g.etykieta}>
                                        <div className={styles.sekcjaNaglowek}><span><g.Ikona size={12} /> {g.etykieta}</span></div>
                                        {g.pozycje.slice(0, LIMIT_W_OKNIE).map((p) => <Tresc key={`${p.rodzaj}:${p.id}`} p={p} Ikona={g.Ikona} />)}
                                    </section>
                                ))}
                                {ladowanie && tresci.length === 0 && <Szkielet />}
                                {blad && <p className={styles.blad} role="alert">{blad}</p>}
                                {!ladowanie && !blad && liczbaWynikow === 0 && (
                                    <p className={styles.podpowiedz}>Nic nie pasuje do „{fraza.trim()}” w treściach i stronach, do których masz dostęp.</p>
                                )}
                            </>
                        )}
                    </div>

                    <footer className={styles.stopka}>
                        <span className={styles.stopkaSkrot}><kbd>Esc</kbd> zamknij · <kbd><FiCornerDownLeft size={10} /></kbd> wszystkie wyniki</span>
                        <button type="button" className={styles.stopkaPrzycisk} onClick={otworzSzuflade} disabled={pusteFraza}>
                            Pokaż wszystkie wyniki{liczbaWynikow > 0 ? ` (${liczbaWynikow})` : ""}
                        </button>
                    </footer>
                </div>
            </div>

            <SideDrawer
                isOpen={szuflada}
                onClose={() => setSzuflada(false)}
                title="Wyniki wyszukiwania"
                subtitle={`„${fraza.trim()}” · ${liczbaWynikow} ${liczbaWynikow === 1 ? "trafienie" : "trafień"}`}
                icon={<FiSearch />}
                width="560px"
            >
                <div className={styles.szuflada}>
                    {dopasowaneKafelki.length > 0 && (
                        <section className={styles.sekcja} aria-label="Strony i ustawienia — wszystkie">
                            <div className={styles.sekcjaNaglowek}><span><FiGrid size={12} /> Strony i ustawienia ({dopasowaneKafelki.length})</span></div>
                            {dopasowaneKafelki.map((k) => <Kafelek key={k.id} k={k} />)}
                        </section>
                    )}
                    {grupy.map((g) => (
                        <section key={g.rodzaj} className={styles.sekcja} aria-label={`${g.etykieta} — wszystkie`}>
                            <div className={styles.sekcjaNaglowek}><span><g.Ikona size={12} /> {g.etykieta} ({g.pozycje.length})</span></div>
                            {g.pozycje.map((p) => <Tresc key={`${p.rodzaj}:${p.id}`} p={p} Ikona={g.Ikona} />)}
                        </section>
                    ))}
                    {liczbaWynikow === 0 && !ladowanie && (
                        <p className={styles.podpowiedz}>Brak trafień dla „{fraza.trim()}”.</p>
                    )}
                </div>
            </SideDrawer>
        </>
    );
};

export default WyszukiwarkaGlobalna;
